import { SourceError } from "../core/errors.js";

export interface XPost {
  id: string;
  text: string;
  created_at: number;
  reply_count: number;
  like_count: number;
  retweet_count: number;
  in_reply_to_id?: string;
  reply_delta_ms?: number;
}

export interface XSourceConfig {
  apiKey: string;
  baseUrl?: string;
  timeout: number;
  maxRetries: number;
}

/**
 * Generic X / Twitter adapter. Talks to the `twitterapi.io` bridge by
 * default but accepts any compatible endpoint via `baseUrl`.
 */
export class XSource {
  private readonly baseUrl: string;
  private readonly apiKey: string;
  private readonly timeout: number;
  private readonly maxRetries: number;

  constructor(config: XSourceConfig) {
    this.apiKey = config.apiKey;
    this.baseUrl = config.baseUrl ?? "https://api.twitterapi.io";
    this.timeout = config.timeout;
    this.maxRetries = config.maxRetries;
  }

  async getRecentPosts(
    handle: string,
    opts: { limit?: number } = {}
  ): Promise<XPost[]> {
    const limit = opts.limit ?? 100;
    const url = new URL(`${this.baseUrl}/twitter/user/last_tweets`);
    url.searchParams.set("userName", handle);

    const raw = (await this.fetchWithRetry(url)) as Record<string, unknown>;
    const tweets = Array.isArray(raw.tweets)
      ? raw.tweets
      : Array.isArray((raw.data as Record<string, unknown>)?.tweets)
        ? (raw.data as { tweets: unknown[] }).tweets
        : [];

    const posts: XPost[] = [];
    for (const t of tweets.slice(0, limit)) {
      const obj = t as Record<string, unknown>;
      const createdAt = parseDate(obj.createdAt);
      if (!createdAt) continue;

      posts.push({
        id: String(obj.id ?? ""),
        text: String(obj.text ?? ""),
        created_at: createdAt,
        reply_count: Number(obj.replyCount ?? 0),
        like_count: Number(obj.likeCount ?? 0),
        retweet_count: Number(obj.retweetCount ?? 0),
        in_reply_to_id: obj.inReplyToId ? String(obj.inReplyToId) : undefined,
      });
    }

    // Compute reply deltas if we have the info.
    for (let i = 0; i < posts.length; i++) {
      const p = posts[i]!;
      if (p.in_reply_to_id) {
        const parent = posts.find((x) => x.id === p.in_reply_to_id);
        if (parent) {
          p.reply_delta_ms = (p.created_at - parent.created_at) * 1000;
        }
      }
    }

    return posts;
  }

  private async fetchWithRetry(url: URL): Promise<unknown> {
    let lastError: unknown;
    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), this.timeout);
        const res = await fetch(url, {
          signal: controller.signal,
          headers: { "X-API-Key": this.apiKey },
        });
        clearTimeout(timer);

        if (res.status === 429) {
          const retryAfter = Number(res.headers.get("retry-after") ?? "2");
          await sleep(retryAfter * 1000);
          continue;
        }
        if (!res.ok) {
          throw new SourceError("x", `HTTP ${res.status}`, res.status);
        }
        return await res.json();
      } catch (err) {
        lastError = err;
        if (attempt < this.maxRetries) {
          await sleep(500 * (attempt + 1));
        }
      }
    }
    throw lastError instanceof Error
      ? new SourceError("x", lastError.message)
      : new SourceError("x", "unknown error");
  }
}

function parseDate(v: unknown): number | null {
  if (typeof v === "number") return v;
  if (typeof v !== "string") return null;
  const parsed = Date.parse(v);
  return isNaN(parsed) ? null : Math.floor(parsed / 1000);
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
